import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  signal,
  Signal,
} from '@angular/core';
import { BrButton } from '@govbr-ds/webcomponents-angular/standalone';
import { ConfirmacaoVagaService } from '../../../../shared/services/confirmacao-vaga.service';
import { Ciclo } from '../../../../shared/models/response/ciclo.model';
import { CardCollapseComponent } from '../../../../shared/components/collapse/card-collapse.component';
import { BrSelectCustomComponent } from '../../../../shared/components/br-select-custom/br-select-custom.component';
import { AlertService } from '../../../../shared/services/alert.service';
import { LoadingService } from '../../../../shared/services/loading.service';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { endOfDay, parse, startOfDay } from 'date-fns';

@Component({
  selector: 'app-atribuir-ine-step',
  templateUrl: './atribuir-ine-step.component.html',
  styleUrls: ['./atribuir-ine-step.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BrButton,
    CardCollapseComponent,
    BrSelectCustomComponent,
  ],
})
export class AtribuirIneStepComponent implements OnInit {
  @Input() onAtribuirINE!: EventEmitter<any>;
  @Input() onStepChange!: EventEmitter<number>;
  @Input() onSubmitting!: EventEmitter<boolean>;
  @Input() form!: FormGroup;
  @Input() cicloSelected!: Ciclo | null;
  @Input() confirmacaoVaga!: any;

  private formBuilder = inject(FormBuilder);
  private alertService = inject(AlertService);
  private confirmacaoVagaService = inject(ConfirmacaoVagaService);
  private loadingService = inject(LoadingService);
  private cdr = inject(ChangeDetectorRef);

  index: number = 0;
  vagas: any[] = [];
  equipes: any[] = [];
  equipesVagas: any[] = [];
  isSubmitting = false;
  totalVagasIne = signal<number>(0);

  get modalidades(): any[] {
    return this.confirmacaoVaga?.conteudo
      ? JSON.parse(this.confirmacaoVaga.conteudo)
      : [];
  }

  get totalVagasConfirmadas(): number {
    return this.modalidades.reduce(
      (total, modalidade) => total + Number(modalidade.data?.reduce((subTotal: any, item: any) => subTotal + Number(item.vagasConfirmadas || 0), 0) || 0),
      0,
    );
  }

  setTotalVagasIne(): void {
    this.totalVagasIne.set(
      this.vagasEquipe.controls
        .map((control: any) => {
          return (control as FormArray).controls.filter((vagaControl) => {
            return !!vagaControl.get('ine')?.value;
          }).length;
        })
        .flat()
        .reduce((total, count) => total + count, 0) || 0,
    );
  }

  get vagasEquipe(): FormArray {
    return this.form.get('vagasEquipe') as FormArray;
  }

  getIne(groupIndex: number, vagaIndex: number): any {
    return this.equipesVagas[groupIndex][vagaIndex]?.find((option: any) => {
      return option.selected;
    })?.equipe;
  }

  getVagaEquipeModalidadeIndex(modalidadeId: number): number {
    return this.vagasEquipe.controls.findIndex((control) => {
      return control.get('modalidadeId')?.value === modalidadeId;
    });
  }

  getEquipesVagas(indexGroup: number, indexVaga: number): any[] {
    return this.equipesVagas[indexGroup][indexVaga].filter((option: any) => {
      return !option.disabled;
    });
  }

  ngOnInit(): void {
    this.onSubmitting.subscribe((isSubmitting: boolean) => {
      this.isSubmitting = isSubmitting;
      this.cdr.detectChanges();
    });

    this.loadVagas();
  }

  // A confirmação efetivada continua editável enquanto o período estiver aberto (PGPP-756).
  isPeriodoVigenteForEnable(): boolean {
    if (this.cicloSelected?.confirmacaoVaga) {
      const now = new Date();
      const dtInicio = startOfDay(
        parse(
          this.cicloSelected.confirmacaoVaga.inicio,
          'yyyy-MM-dd',
          new Date(),
        ),
      );
      const dtFim = endOfDay(
        parse(this.cicloSelected.confirmacaoVaga.fim, 'yyyy-MM-dd', new Date()),
      );
      return now >= dtInicio && now <= dtFim;
    }
    return false;
  }

  next(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    this.onSubmitting.emit(true);
    this.onAtribuirINE.emit(this.buildRequest());
  }

  prev(): void {
    if (this.isPeriodoVigenteForEnable()) {
      this.alertService.confirm(
        'Ao retornar, as seleções de INE feitas nesta tela serão perdidas. Deseja continuar?',
        () => {
          this.onStepChange.emit(1);
        },
      );
    } else {
      this.onStepChange.emit(1);
    }
  }

  incrementIndex(): number {
    return this.index++;
  }

  setEquipe(equipe: any, indexGroup: number, indexVaga: number): void {
    const equipesSelecionadas = (
      this.vagasEquipe.at(indexGroup) as FormArray
    ).controls
      .map((control) => control.get('ine')?.value)
      .filter((value) => !!value);

    const isSelected = (i: number, j: number, value: any) => {
      return (
        (this.vagasEquipe.at(i) as FormArray)?.at(j)?.get('ine')?.value == value
      );
    };

    for (let i = 0; i < this.equipesVagas.length; i++) {
      for (let j = 0; j < this.equipesVagas[i].length; j++) {
        this.equipesVagas[i][j] = this.equipesVagas[i][j].map((option: any) => {
          if (equipesSelecionadas.includes(option.value)) {
            return {
              ...option,
              disabled: !isSelected(i, j, option.value),
              selected: isSelected(i, j, option.value),
            };
          } else {
            return {
              ...option,
              disabled: false,
              selected: isSelected(i, j, option.value),
            };
          }
        });
      }
    }

    this.setTotalVagasIne();
    this.cdr.detectChanges();
  }

  private loadVagas(): void {
    this.loadingService.open();
    this.confirmacaoVagaService
      .fetchVagasCiclo(this.cicloSelected?.id as number)
      .subscribe({
        next: (response) => {
          this.loadingService.close();
          this.vagas = [];

          const vagasByModalidade: any[] = [];

          this.vagasEquipe.clear();

          // Somente leitura (confirmada e fora do período): mostra a foto gravada na confirmação.
          if (!this.confirmacaoVaga.rascunho && !this.isPeriodoVigenteForEnable()) {
            response =
              this.confirmacaoVaga.confirmacoesVagaIne?.map((cv: any) => {
                return cv.vagaCiclo;
              }) || [];
          }

          response.forEach((vaga: any) => {
            const index = vagasByModalidade.findIndex(
              (v) =>
                v.idModalidade == vaga.vagaPrograma.modalidadeFinanciamento.id,
            );

            const totaVagas = this.modalidades.find(
              (m) =>
                m.idModalidade == vaga.vagaPrograma.modalidadeFinanciamento.id,
            ).data.find((d: any) => d.tipoEquipe == vaga.vagaPrograma.tipoEquipe.nome)?.vagasConfirmadas;

            if (
              totaVagas == 0 ||
              (vagasByModalidade[index] &&
                vagasByModalidade[index].vagasTipoEquipe.find((vte: any) => vte.tipoEquipe == vaga.vagaPrograma.tipoEquipe.nome)?.vagas.length >= totaVagas)
            ) {
              return;
            }

            if (index > -1) {
              vagasByModalidade[index].vagas.push(vaga);
              vagasByModalidade[index].vagasTipoEquipe.find((vte: any) => vte.tipoEquipe == vaga.vagaPrograma.tipoEquipe.nome)?.vagas.push(vaga);
            } else {
              vagasByModalidade.push({
                idModalidade: vaga.vagaPrograma.modalidadeFinanciamento.id,
                nomeModalidade: vaga.vagaPrograma.modalidadeFinanciamento.nome,
                vagasTipoEquipe: [{
                  tipoEquipe: vaga.vagaPrograma.tipoEquipe.nome,
                  vagas: [vaga],
                }],
                vagas: [vaga],
              });
            }
          });

          vagasByModalidade.forEach((vagaByModalidade) => {
            let index = this.getVagaEquipeModalidadeIndex(
              vagaByModalidade.idModalidade,
            );

            if (index === -1) {
              this.vagasEquipe.push(this.formBuilder.array([]));
              index = this.vagasEquipe.length - 1;
            }

            vagaByModalidade.vagas.forEach((vaga: any) => {
              const value =
                this.confirmacaoVaga.confirmacoesVagaIne?.find((cv: any) => {
                  return vaga.id == cv?.vagaCiclo?.id;
                }) || false;

              (this.vagasEquipe.at(index) as FormArray).push(
                this.formBuilder.group({
                  id: [vaga.id],
                  ine: [value?.vagaCicloIne?.id || null],
                  modalidadeId: [vagaByModalidade.idModalidade],
                }),
              );
            });
          });

          this.loadEquipesVagas(vagasByModalidade);
        },
        error: (error) => {
          this.loadingService.close();
          console.error('Erro ao carregar vagas:', error);
          this.alertService.error('Erro ao carregar vagas. Tente novamente.');
        },
      });
  }

  private loadEquipesVagas(vagas: any[]): void {
    this.loadingService.open();
    this.confirmacaoVagaService
      .fetchEquipesVagas(this.cicloSelected?.id as number)
      .subscribe({
        next: (response) => {
          this.loadingService.close();
          this.equipes = response;

          if (this.equipes.length === 0) {
            this.alertService.warning(
              'Não há INEs disponíveis para o nível da vaga selecionada (Municipal/Estadual/DSEI). A vinculação de INE é opcional e você pode prosseguir com a confirmação.',
            );
          }

          let valuesAdded: any[] =
            this.confirmacaoVaga.confirmacoesVagaIne?.map(
              (cv: any) => cv.vagaCicloIne?.id,
            ) || [];

          this.vagas = vagas.map((vagaByModalidade, i) => {
            vagaByModalidade.vagas.map((vaga: any, j: number) => {
              if (!this.equipesVagas[i]) {
                this.equipesVagas[i] = [];
              }

              let value: any = null;

              this.equipesVagas[i][j] = this.equipes.filter((ev: any) => {
                return ev.tipoEquipe.id === vaga.vagaPrograma.tipoEquipe.id;
              }).map((e) => {
                value =
                  this.confirmacaoVaga.confirmacoesVagaIne
                    ?.filter((confirmacao: any) => confirmacao.confirmacao)
                    .find((cv: any) => {
                      return (
                        vaga.id == cv?.vagaCiclo?.id &&
                        e.id == cv?.vagaCicloIne?.id
                      );
                    }) || null;

                return {
                  label: [e.nuIne, e.nomeEquipe].join(' - '),
                  value: e.id.toString(),
                  selected: value?.vagaCicloIne?.id == e.id,
                  disabled:
                    valuesAdded.includes(e.id) &&
                    value?.vagaCicloIne?.id !== e.id,
                  equipe: e,
                } as any;
              });
            });

            return vagaByModalidade;
          });

          if (!this.isPeriodoVigenteForEnable()) {
            this.form.disable();
          }

          this.setTotalVagasIne();
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.loadingService.close();
          console.error('Erro ao carregar equipes:', error);
          this.alertService.error('Erro ao carregar equipes. Tente novamente.');
        },
      });
  }

  private buildRequest(): any {
    return {
      vagasIne: this.vagasEquipe.controls
        .map((control) => {
          return (control as FormArray).controls.map((vagaControl) => {
            return {
              vagaCicloId: vagaControl.get('id')?.value,
              vagaCicloIneId: vagaControl.get('ine')?.value,
            };
          });
        })
        .flat(),
    };
  }
}
